import React, {useState, useEffect, useContext, useRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import useScriptLoader from '../hooks/use-script-loader'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useAuth from '../hooks/use-auth'
import {MRT_API_BASEPATH} from '../ssr/constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {
    useUsid,
    useShopperBasketsMutationHelper,
    useShopperCustomersMutation,
    useAuthHelper,
    AuthHelpers,
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useLocaleLanguage} from '../hooks/use-locale-custom'
import {BRAND_KEY} from '../brand'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useLocation} from 'react-router-dom'
import useUtils from '../custom-hooks/use-utils-helper'
import {useWishList} from '../hooks/use-wish-list'
import {salesForceToGigyaLocaleMapping} from '../utils/gigya/mapping'
import {getMrtApiBasepath} from '../ssr/helpers'
import {HStack} from '@salesforce/retail-react-app/app/components/shared/ui'
import Feedback from '../components/feedback'
import useGigyaSSR from '../custom-hooks/use-gigya-ssr'
import GigyaRegisterController from '../utils/gigya/registration'
import GigyaBasicFormController from '../utils/gigya/lite-registration'

/**
 * This is the global state for gigya sdk, we use this throughout the site.
 */
export const GigyaContext = React.createContext()
const GigyaProvider = ({children}) => {
    const [gigyaObject, setGigyaObject] = useState(null)
    const [onErrorGigya, setOnErrorGigya] = useState(false)
    const {handleTokenResponse} = useAuth()
    const {locale, site} = useMultiSite()
    const lang = useLocaleLanguage()
    const {usid} = useUsid()
    const appOrigin = useAppOrigin()
    const {isFetched} = useCurrentBasket()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem',
    )
    const {data: wishListData} = useWishList()
    const {data: customer} = useCurrentCustomer()
    const location = useLocation()
    const {isRegistrationConfirmation} = useUtils().currentPage(location.pathname)
    const ssrGigyaMethods = useGigyaSSR()
    const {getAccountInfo} = ssrGigyaMethods

    const apiKey = getConfig()?.customParams?.gigyaApiKey

    const gigyaLocale = useMemo(() => {
        const mapping = salesForceToGigyaLocaleMapping()
        return mapping?.[locale.alias] ? mapping?.[locale.alias] : lang
    }, [locale.alias])

    const sdkURLGigya = apiKey
        ? `https://cdns.eu1.gigya.com/JS/gigya.js?apikey=${apiKey}&lang=${gigyaLocale}`
        : null

    const [sdkURLGigyaLoading, sdkURLGigyaError] = useScriptLoader(sdkURLGigya)
    const isLoginCallbackAddedd = useRef(false)

    const [isLogged, setIsLogged] = useState(false)
    const navigate = useNavigation()
    const logout = useAuthHelper(AuthHelpers.Logout)

    useEffect(() => {
        if (isFetched && isLogged) {
            const guestBasket = localStorage.getItem('guestBasket')
            if (guestBasket !== null && guestBasket !== 'undefined') {
                const parsedGuestBakest = JSON.parse(guestBasket)

                const shouldMergeBasket = parsedGuestBakest?.productItems?.length > 0
                if (shouldMergeBasket) {
                    try {
                        const productItems = parsedGuestBakest?.productItems?.map((el) => ({
                            productId: el.productId,
                            price: el.price,
                            quantity: el.quantity,
                            c_MaterialGroup: el?.c_MaterialGroup,
                            c_categoryName: el?.c_categoryName,
                            c_subModelIdentifier: el?.c_subModelIdentifier,
                            c_defaultCategoryName: el?.c_c_defaultCategoryName,
                            c_defaultName: el?.c_defaultName,
                            c_prdhaCategory: el?.c_prdhaCategory,
                            c_cost: el?.c_cost,
                            c_officinaConfigurationData: el?.c_officinaConfigurationData,
                            c_officinaConfigurationId: el?.c_officinaConfigurationId,
                            c_officinaConfigurationImage: el?.c_officinaConfigurationImage,
                            ...(el?.c_isSubscription === 'true' && {
                                c_productSetID: el?.c_productSetID,
                                c_isSubscription: true,
                                c_isSubscriptionRequest: true,
                                optionItems: [
                                    {
                                        optionId: 'isSubscription',
                                        optionValueId: 'isSubscription',
                                    },
                                ],
                            }),
                            ...(!!el?.c_isBundle === true && {
                                c_UUIDBundle: el?.c_UUIDBundle,
                                c_isBundle: true,
                                c_productSetID: el?.c_productSetID,
                                c_isBundleRequest: true,
                            }),
                        }))

                        addItemToNewOrExistingBasket(productItems)
                    } catch (error) {
                        console.error('error', error)
                    }
                }
            }
            localStorage.removeItem('guestBasket')
            const isCheckoutPage = window.location.pathname.includes('/checkout')
            if (isCheckoutPage) navigate('/checkout')
        }
    }, [isFetched])

    useEffect(() => {
        if (wishListData && isLogged) {
            const guestWishList = localStorage.getItem('guestWishList')

            // Handle guest wishlist
            if (guestWishList !== null && guestWishList !== 'undefined') {
                const parsedGuestWishList = JSON.parse(guestWishList)

                const guestItems = parsedGuestWishList?.customerProductListItems || []
                const isProductInWishlist = (productId) => {
                    return wishListData?.customerProductListItems?.some(
                        (item) => item.productId === productId,
                    )
                }

                const filteredGuestItems = guestItems.filter(
                    (item) => !isProductInWishlist(item.productId),
                )
                filteredGuestItems.forEach(async (item) => {
                    try {
                        const body = {
                            productId: item.productId,
                            quantity: item.quantity,
                            priority: item.priority,
                            type: item.type,
                            public: true,
                        }

                        const parameters = {
                            customerId: customer?.customerId, // Use the current customer's ID
                            listId: wishListData?.id, // Use the current wishlist ID
                        }

                        await createCustomerProductListItem({
                            parameters: parameters,
                            body: body,
                        })

                        localStorage.removeItem('guestWishList')
                    } catch (error) {
                        console.error('Error adding item to wishlist:', error)
                    }
                })
            }
        }
    }, [wishListData])

    useEffect(() => {
        if (sdkURLGigyaError) {
            setOnErrorGigya(true)
        }
    }, [sdkURLGigyaError])

    useEffect(() => {
        window.DLG = window.DLG || {}
        window.DLG.gigya = {
            brand: BRAND_KEY,
        }
    }, [])

    useEffect(() => {
        if (!sdkURLGigyaLoading && !sdkURLGigyaError && window.gigya) {
            setGigyaObject(window.gigya)
        }
    }, [sdkURLGigyaLoading, sdkURLGigyaError])

    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        if (errorMessage) {
            setTimeout(() => {
                setErrorMessage('')
            }, 5000)
        }
    }, [errorMessage])

    const logoutAndError = async () => {
        gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()
        await logout.mutateAsync()

        const endpoint = origin + getMrtApiBasepath('/logout')
        await fetch(`${endpoint}`).then(() => {
            setErrorMessage('An error occurred. Please try to login again.')
        })
    }

    useEffect(() => {
        if (!gigyaObject) return
        if (isLoginCallbackAddedd.current) return

        gigyaObject.accounts.addEventHandlers({
            onLogin: async (gigyaRes) => {
                const accountInfo = await getAccountInfo(gigyaRes?.UID, null)
                const lang = window.gigya.thisScript.lang.originalLang
                const countryCode = window.gigya.thisScript.globalConf.countryCodeMapping[lang]
                const profiledCommunicationEl =
                    window.gigya.thisScript.globalConf.consentMapping[countryCode]
                // Show comprete registration screen if no preferences are set
                if (
                    !accountInfo?.preferences ||
                    Object.keys(accountInfo.preferences).length === 0 ||
                    (profiledCommunicationEl &&
                        !(profiledCommunicationEl in accountInfo.preferences))
                ) {
                    GigyaRegisterController.showCompleteRegistration({
                        gigyaObject,
                        event: gigyaRes,
                        gigyaLocale,
                        GigyaRegisterController,
                        GigyaBasicFormController,
                    })

                    return false
                }

                const guestCustomerId = localStorage.getItem('guestCustomerId')
                isLoginCallbackAddedd.current = true

                const myHeaders = new Headers()
                myHeaders.append('Content-Type', 'application/json')
                const raw = JSON.stringify({
                    loginId: gigyaRes.UID,
                    idpOrigin: 'gigya-login',
                    customerInfo: gigyaRes,
                    localeId: locale.id,
                    ...(usid && {usid}),
                    guestCustomerId,
                    siteId: site.id,
                })

                // If the UID is not present in the Gigya response, log out the user from Gigya,
                // clear the session on the backend, and navigate to the login page.
                if (!gigyaRes.UID) {
                    await logoutAndError()
                    return
                }

                const requestOptions = {
                    method: 'POST',
                    headers: myHeaders,
                    body: raw,
                    redirect: 'follow',
                }

                const tokenResponse = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-trusted-system-access-token`,
                    requestOptions,
                )
                const response = await tokenResponse?.json()

                if (response.error || !response.access_token) {
                    await logoutAndError()
                    console.error('Error retrieving TSOB token', response.error)
                    return
                }

                handleTokenResponse(response)
                setIsLogged(true)

                const preventDefaultRedirect = localStorage.getItem('preventGigyaDefaultRedirect')
                if (preventDefaultRedirect === 'true') {
                    localStorage.removeItem('preventGigyaDefaultRedirect')
                    return
                }

                const redirectUrl = localStorage.getItem('loginRedirect')
                if (redirectUrl) {
                    localStorage.removeItem('loginRedirect')
                    window.location.href = redirectUrl
                    return
                }

                if (isRegistrationConfirmation) {
                    return
                }
                const isCheckoutPage = window.location.pathname.includes('/checkout')
                if (location?.state?.directedFrom) {
                    navigate(location.state.directedFrom)
                } else if (!isCheckoutPage) {
                    navigate('/account')
                }
            },
        })
    }, [gigyaObject])

    return (
        <GigyaContext.Provider
            value={{gigyaObject: gigyaObject, isError: onErrorGigya, gigyaLocale}}
        >
            {errorMessage && (
                <HStack>
                    <Feedback
                        position="fixed"
                        top={{base: '120px', lg: '180px'}}
                        right={{base: '27px', lg: '100px'}}
                        zIndex="9999"
                        maxWidth="400px"
                        message={errorMessage}
                        variant="removeFeedbackWishlist"
                        status="error"
                        backgroundColor="background.danger"
                        enableIcon={false}
                    />
                </HStack>
            )}
            {children}
        </GigyaContext.Provider>
    )
}

export default GigyaProvider

GigyaProvider.propTypes = {
    children: PropTypes.node.isRequired,
}

/**
 * Custom React hook to use the Gigya SDK
 * @returns {{gigyaObject: Object, onErrorGigya: Boolean}}
 */
export const useGigya = () => {
    const context = useContext(GigyaContext)
    if (!context) {
        throw new Error('useGigya must be used within a GigyaProvider')
    }
    return context
}
